import { apiClient } from "@shared/lib/api-client";

/**
 * kie 系图片模型（v3.0 / gpt-image-2 / nano-banana-2 / nano-banana-pro）的
 * POST /api/image-generator 不再等出图，立刻返回任务 id，前端轮询 status 拿结果。
 * fal 系（v1.0 / v2.0）仍在一个请求里出图，返回完整结果。
 */
export type ImageTaskSubmitted = { id: string; status: "PENDING" };

type ImageTaskStatusResponse = {
	id: string;
	status: "PENDING" | "PROCESSING" | "SUCCESS" | "FAILED";
	imageUrls?: string[];
	errorMessage?: string;
	errorCode?: string;
};

export type ImageTaskError = Error & { code?: string; status?: number };

const POLL_INTERVAL_MS = 3_000;
/** 连续这么多次查不到（断网、5xx）就放弃。行还在库里，下次打开生成器页会续上 */
const MAX_CONSECUTIVE_POLL_ERRORS = 5;
/** 前端最多等这么久，上游卡住时别让按钮一直转圈；服务端 24 小时后才判失败 */
const MAX_WAIT_MS = 10 * 60 * 1000;

function sleep(ms: number): Promise<void> {
	return new Promise((resolve) => setTimeout(resolve, ms));
}

function taskError(
	message: string,
	code: string,
	status?: number,
): ImageTaskError {
	const error = new Error(message) as ImageTaskError;
	error.code = code;
	error.status = status;
	return error;
}

export function isImageTaskSubmitted(
	value: unknown,
): value is ImageTaskSubmitted {
	return (
		typeof value === "object" &&
		value !== null &&
		(value as { status?: unknown }).status === "PENDING" &&
		typeof (value as { id?: unknown }).id === "string"
	);
}

/**
 * 每 3 秒查一次，SUCCESS 返回出图地址，FAILED 抛出带后端错误码的 Error
 * （NSFW_CONTENT_DETECTED / NO_MEDIA_GENERATED / SERVER_ERROR，和同步接口一致）。
 * 每次请求只有几百毫秒：手机切后台断掉的只是其中一次查询，回到前台接着查，
 * 不会再像一个请求等两分钟那样整单报 Load failed。
 */
export async function waitForImageTask(id: string): Promise<string[]> {
	const deadline = Date.now() + MAX_WAIT_MS;
	let consecutiveErrors = 0;

	for (;;) {
		await sleep(POLL_INTERVAL_MS);

		let task: ImageTaskStatusResponse | null = null;
		try {
			const response = await apiClient["image-generator"].status[
				":id"
			].$get({ param: { id } });
			if (response.status === 404 || response.status === 403) {
				throw taskError(
					"Image task not found",
					"TASK_NOT_FOUND",
					response.status,
				);
			}
			if (response.ok) {
				task = (await response.json()) as ImageTaskStatusResponse;
				consecutiveErrors = 0;
			}
		} catch (error) {
			if ((error as ImageTaskError).code === "TASK_NOT_FOUND") {
				throw error;
			}
		}

		if (task?.status === "SUCCESS") {
			return task.imageUrls ?? [];
		}
		if (task?.status === "FAILED") {
			throw taskError(
				task.errorMessage || "Failed to generate image",
				task.errorCode ?? "SERVER_ERROR",
			);
		}
		if (!task) {
			consecutiveErrors += 1;
			if (consecutiveErrors >= MAX_CONSECUTIVE_POLL_ERRORS) {
				throw taskError("Lost connection to task", "LOST_CONNECTION");
			}
		}
		// 放在查询之后判断：切后台很久再回来，至少先查一次再决定要不要放弃
		if (Date.now() > deadline) {
			throw taskError("Lost connection to task", "LOST_CONNECTION");
		}
	}
}
